import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  FormTitle,
  Input,
  Modal,
  Select,
  ShowInput,
  SubContent,
  Wrapper,
  message,
  useBreadcrumbRoutes,
} from '@maxtropy/components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  apiV2EnergyConsumptionPlanGetDelayEnergyConsumptionDetailPost,
  V2EnergyConsumptionPlanGetDelayEnergyConsumptionDetailPostResponse,
  apiV2EnergyConsumptionPlanAddDelayEnergyConsumptionPost,
  apiV2EnergyConsumptionPlanGetStaffOrgMappingPost,
  V2EnergyConsumptionPlanGetAreaListPostResponse,
  apiV2EnergyConsumptionPlanGetAreaListPost,
  apiV2EnergyConsumptionAdminListPost,
  V2EnergyConsumptionAdminListPostResponse,
  apiV2EnergyConsumptionPlanUpdateDelayEnergyConsumptionPost,
} from '@maxtropy/device-customer-apis-v2';
import styles from './index.module.scss';
import { InputNumber, Space, Upload as AntUpload } from 'antd';
import dayjs from 'dayjs';
import type { UploadFile } from 'antd/es/upload/interface';
import { UploadChangeParam } from 'antd/lib/upload';

const { RangePicker } = DatePicker;

export type DelayEnergyConsumptionDetail = Exclude<
  V2EnergyConsumptionPlanGetDelayEnergyConsumptionDetailPostResponse['delayEnergyConsumptionDetailList'],
  undefined
>[number];

const EnergyDeferredApplication = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const copyId = searchParams.get('copyId');

  const [form] = Form.useForm();
  const [areaOp, setAreaOp] = useState<V2EnergyConsumptionPlanGetAreaListPostResponse['list']>([]);
  const [mediaOp, setMediaOp] = useState<V2EnergyConsumptionAdminListPostResponse['list']>([]);

  const [detailList, setDetailList] = useState<any[]>([{}]);
  const [orgInfo, setOrgInfo] = useState<Record<string, any>>();
  const [disabled, setDisabled] = useState<boolean>(false);
  const [importLoading, setImportLoading] = useState<boolean>(false);

  useEffect(() => {
    apiV2EnergyConsumptionPlanGetStaffOrgMappingPost({}).then(res => {
      if (res) {
        form.setFieldsValue({
          ownerOrgId: res.ownerOrgId,
          ownerOrgName: res.ownerOrgName,
        });
        setOrgInfo({
          ownerOrgId: res.ownerOrgId,
          ownerOrgName: res.ownerOrgName,
        });
      } else {
        form.setFieldsValue({
          ownerOrgName: '-',
        });
        setDisabled(true);
        message.error('当前申请人无权限申请');
      }
    });
  }, []);

  useEffect(() => {
    if (!orgInfo?.ownerOrgId) return;
    apiV2EnergyConsumptionPlanGetAreaListPost({ id: orgInfo?.ownerOrgId }).then(res => {
      setAreaOp(res.list);
    });
  }, [orgInfo]);

  useEffect(() => {
    apiV2EnergyConsumptionAdminListPost({}).then(res => {
      setMediaOp(res.list ?? []);
    });
  }, []);

  useEffect(() => {
    const fetchId = id || copyId;
    if (fetchId) {
      apiV2EnergyConsumptionPlanGetDelayEnergyConsumptionDetailPost({
        id: fetchId,
      }).then(res => {
        if (res) {
          const detailListData = (res.delayEnergyConsumptionDetailList || []).map(i => ({
            areaIdList: i.areaList?.map(a => a.id) ?? [],
            duration: i.duration,
            mediumIdList: i.mediumList?.map(m => m.id),
            workers: i.workers,
            content: i.content,
            startTime: i.startTime,
            endTime: i.endTime,
            times: [dayjs(i.startTime), dayjs(i.endTime)],
          }));
          form.setFieldsValue({
            delayEnergyConsumptionDetailList: detailListData,
          });
          setDetailList(detailListData);
        }
      });
    }
  }, [id, copyId]);

  // 添加明细
  const addDetail = () => {
    const newDetailList = [...detailList, {}];
    setDetailList(newDetailList);
    form.setFieldsValue({
      delayEnergyConsumptionDetailList: newDetailList,
    });
  };

  // 删除明细
  const removeDetail = (index: number) => {
    const newDetailList = [...detailList];
    newDetailList.splice(index, 1);
    setDetailList(newDetailList);
    form.setFieldsValue({
      delayEnergyConsumptionDetailList: newDetailList,
    });
  };

  // 复制明细
  const copyDetail = (index: number) => {
    const detailToCopy = { ...detailList[index] };

    const newDetailList = [...detailList, detailToCopy];
    setDetailList(newDetailList);
    form.setFieldsValue({
      delayEnergyConsumptionDetailList: newDetailList,
    });
  };
  // 时间变化时计算时长
  const onTimeChange = (index: number, times: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    if (!times || !times[0] || !times[1]) return;

    const [start, end] = times;
    if (start && end) {
      // 计算时长（小时），保留两位小数
      const duration = parseFloat(((end.valueOf() - start.valueOf()) / (1000 * 60 * 60)).toFixed(2));

      const newDetailList: any = [...detailList];
      newDetailList[index] = {
        ...newDetailList[index],
        times,
        startTime: start.valueOf(),
        endTime: end.valueOf(),
        duration,
      };

      setDetailList(newDetailList);
      form.setFieldsValue({
        delayEnergyConsumptionDetailList: newDetailList,
      });
    }
  };

  // 当表单项发生变化时同步更新detailList状态
  const onValuesChange = (changedValues: any, allValues: any) => {
    const fullValues = form.getFieldsValue(true);
    // 如果变化的是明细列表，则更新detailList状态
    if (allValues.delayEnergyConsumptionDetailList !== undefined) {
      setDetailList(fullValues.delayEnergyConsumptionDetailList);
    }
  };

  const onFinish = (isSave: boolean) => {
    form.validateFields().then(() => {
      const params = {
        ...form.getFieldsValue(true),
        type: isSave ? 1 : 0,
        id,
      };
      const api = id
        ? apiV2EnergyConsumptionPlanUpdateDelayEnergyConsumptionPost
        : apiV2EnergyConsumptionPlanAddDelayEnergyConsumptionPost;

      api(params)
        .onError(error => {
          const { errorMessage } = error?.cause;
          Modal.error({
            title: '用能计划冲突',
            width: 480,
            content: (
              <Space direction="vertical" size={4}>
                {errorMessage.split('\n').map((line: string, index: number) => (
                  <div key={index}>{line}</div>
                ))}
              </Space>
            ),
          });
          throw error;
        })
        .then(res => {
          navigate(-1);
        });
    });
  };

  return (
    <Wrapper routes={breadcrumbRoutes?.routes}>
      <FormTitle title="延时用能申请" />
      <Form form={form} onValuesChange={onValuesChange}>
        <SubContent title="基本信息">
          <Form.Item label="申请部门" name="ownerOrgName">
            <Input disabled={true} style={{ width: 464 }} />
          </Form.Item>
        </SubContent>
        <SubContent title="申请明细">
          <Space direction="horizontal" size={16}>
            <Button type="primary" onClick={addDetail} disabled={disabled}>
              添加明细
            </Button>
            <AntUpload
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              action={'/api/v2/energy/consumption/plan/delayEnergyUseImport'}
              showUploadList={false}
              disabled={disabled || importLoading}
              onChange={(uploadFile: UploadChangeParam<UploadFile>) => {
                const { file } = uploadFile;
                if (file.status === 'uploading') {
                  setImportLoading(true);
                }
                if (file.status === 'error') {
                  setImportLoading(false);
                  Modal.error({
                    title: file.response.errorMessage || '未知错误！ 请联系管理员。',
                  });
                }
                if (file.status === 'done') {
                  setImportLoading(false);
                  if (!file.response.success) {
                    Modal.error({
                      title: file.response.msg || '未知错误！ 请联系管理员。',
                    });
                  } else {
                    const delayEnergyConsumptionDetailList: DelayEnergyConsumptionDetail[] =
                      uploadFile?.file?.response?.data?.delayEnergyConsumptionDetailList ?? [];
                    const detailListData = delayEnergyConsumptionDetailList.map(i => ({
                      areaIdList: i.areaList?.map(a => a.id) ?? [],
                      duration: i.duration,
                      mediumIdList: i.mediumList?.map(m => m.id),
                      workers: i.workers,
                      content: i.content,
                      startTime: i.startTime,
                      endTime: i.endTime,
                      times: [dayjs(i.startTime), dayjs(i.endTime)],
                    }));
                    form.setFieldsValue({
                      delayEnergyConsumptionDetailList: detailListData,
                    });
                    setDetailList(detailListData);
                  }
                }
              }}
            >
              <Button type="link" style={{ padding: 0 }} disabled={disabled || importLoading} loading={importLoading}>
                导入
              </Button>
            </AntUpload>
            <Button
              type="link"
              disabled={disabled}
              style={{ padding: 0 }}
              onClick={() => {
                window.open('/api/v2/energy/consumption/plan/downloadDelayEnergyUseTemplate', '__blank');
              }}
            >
              下载导入模板
            </Button>
          </Space>
          <div className={styles.cardContainer}>
            {detailList?.map((item, index) => (
              <div className={styles.card} key={index}>
                <div className={styles.title}>
                  <span>明细{index + 1}</span>
                  <Button type="link" disabled={disabled} style={{ padding: 0 }} onClick={() => copyDetail(index)}>
                    复制
                  </Button>

                  <Button type="link" disabled={disabled} style={{ padding: 0 }} onClick={() => removeDetail(index)}>
                    删除
                  </Button>
                </div>
                <div className={styles.content}>
                  <Form.Item
                    label="区域选择"
                    name={['delayEnergyConsumptionDetailList', index, 'areaIdList']}
                    rules={[{ required: true, message: '请选择区域' }]}
                  >
                    <Select
                      placeholder="请选择区域"
                      mode="multiple"
                      optionFilterProp="label"
                      options={areaOp?.map(a => ({ label: a.zoneName, value: a.id }))}
                    />
                  </Form.Item>
                  <Form.Item
                    label="作业人数"
                    name={['delayEnergyConsumptionDetailList', index, 'workers']}
                    rules={[
                      { required: true, message: '请输入作业人数' },
                      { type: 'number', min: 1, max: 999999, message: '请输入1-999999之间的数字' },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      max={999999}
                      step={1}
                      precision={0}
                      placeholder="请输入"
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item
                    label="作业内容"
                    name={['delayEnergyConsumptionDetailList', index, 'content']}
                    rules={[
                      { required: true, message: '请输入作业内容' },
                      { min: 1, max: 200, message: '请输入1-200个字符' },
                    ]}
                  >
                    <Input.TextArea placeholder="请输入" rows={3} maxLength={200} />
                  </Form.Item>
                  <Form.Item
                    label="用能类型"
                    name={['delayEnergyConsumptionDetailList', index, 'mediumIdList']}
                    rules={[{ required: true, message: '请选择用能类型' }]}
                  >
                    <Checkbox.Group
                      options={mediaOp?.map(media => ({ label: media.energyMediumName, value: media.energyMediumId }))}
                    />
                  </Form.Item>
                  <Form.Item
                    label="开始/结束时间"
                    name={['delayEnergyConsumptionDetailList', index, 'times']}
                    rules={[{ required: true, message: '请选择开始和结束时间' }]}
                  >
                    <RangePicker
                      showTime={{
                        minuteStep: 30,
                      }}
                      format="YYYY-MM-DD HH:mm"
                      onChange={times => onTimeChange(index, times)}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                  <Form.Item label="用能时长">
                    <ShowInput value={item.duration ? item.duration + '小时' : '--'} />
                  </Form.Item>
                </div>
              </div>
            ))}
          </div>
          <Space size={8}>
            <Button type="primary" disabled={disabled} onClick={() => onFinish(true)}>
              提交
            </Button>
            <Button type="primary" disabled={disabled} onClick={() => onFinish(false)}>
              保存草稿
            </Button>
            <Button onClick={() => navigate(-1)}>取消</Button>
          </Space>
        </SubContent>
      </Form>
    </Wrapper>
  );
};

export default EnergyDeferredApplication;
